import { useEffect, useState } from "react";
import { SellarCreditCheckDialog } from "./SellarCreditCheckDialog/SellarCreditCheckDialog";
import { Icon } from "../../../components";
import { useSellarUser } from "../../../hooks";
import { useSellarCurrentBusiness } from "../../../hooks/useSellarCurrentBusiness.shared";
import { formatCurrency } from "@sellar-io/common";
import { twMerge } from "tailwind-merge";
import { useMediaQuery } from "usehooks-ts";
import { useSearchParams } from "react-router";
import { useSignupContext } from "../../../hooks/useSignupContext.shared";
import { useLegalEntityCompanyCreditInfo } from "../../../hooks/api/v2/legalEntity/useLegalEntityCompanyCreditInfo";

export const SellarCreditCheckBanner = ({
  className,
}: {
  className?: string;
}) => {
  return <TwoSellarCreditCheckBanner className={className} />;
};

export const TwoSellarCreditCheckBanner = ({
  className,
}: {
  className?: string;
}) => {
  const [searchParams] = useSearchParams();
  const { user } = useSellarUser();
  const { business } = useSellarCurrentBusiness();
  const { setSignupDialogActive, signupDialogActive } = useSignupContext();
  const userIsSignedIn = Boolean(user?.id);
  const showSellarCreditOnboardingParam = searchParams.get(
    "showSellarCreditOnboarding",
  );
  const shouldOpenSellarCreditDialog =
    showSellarCreditOnboardingParam === "true" && userIsSignedIn;
  const [sellarCreditDialogActive, setSellarCreditDialogActive] = useState(
    shouldOpenSellarCreditDialog,
  );
  const [previousShouldOpenDialog, setPreviousShouldOpenDialog] = useState(
    shouldOpenSellarCreditDialog,
  );
  if (shouldOpenSellarCreditDialog !== previousShouldOpenDialog) {
    setPreviousShouldOpenDialog(shouldOpenSellarCreditDialog);
    if (shouldOpenSellarCreditDialog) setSellarCreditDialogActive(true);
  }

  const isMobile = useMediaQuery("(max-width: 1279px)");

  const { data: companyCreditInfoData } = useLegalEntityCompanyCreditInfo(
    {
      RetailerId: business?.Retailer?.id ?? 0,
    },
    {
      enabled: Boolean(business?.Retailer?.id) && Boolean(user?.id),
    },
  );

  const hasCreditLimitQuote = !!companyCreditInfoData?.creditLimitQuote;
  const formattedCreditLimitQuote = formatCurrency(
    companyCreditInfoData?.creditLimitQuote ?? 0,
  );

  const showBanner =
    ["eligible", "creditCheckNotPerformed"].includes(
      companyCreditInfoData?.status ?? "",
    ) && !!business?.Retailer;

  useEffect(() => {
    if (showSellarCreditOnboardingParam === "true" && userIsSignedIn) {
      // hide sign up dialog if visible
      if (signupDialogActive) {
        setSignupDialogActive(false);
      }
    }
  }, [
    signupDialogActive,
    setSignupDialogActive,
    showSellarCreditOnboardingParam,
    userIsSignedIn,
  ]);

  useEffect(() => {
    if (showSellarCreditOnboardingParam === "true" && !userIsSignedIn) {
      // show sign up dialog
      setSignupDialogActive(true);
    }
  }, [setSignupDialogActive, showSellarCreditOnboardingParam, userIsSignedIn]);

  return (
    <>
      {showBanner && (
        <>
          {isMobile ? (
            <div
              className={twMerge("w-full", className)}
              onClick={() => setSellarCreditDialogActive(true)}
            >
              <div className="flex cursor-pointer flex-col items-center items-start justify-start rounded-lg border border-zinc-300 bg-white shadow-md">
                <div className="flex w-full items-center gap-1 rounded-t-lg bg-gradient-to-br from-[#B557F1] to-[#D213A6] p-2 font-semibold text-white">
                  <Icon
                    icon="SELLAR_NEW"
                    color="white"
                    size={0.7}
                    className="shrink-0"
                  />
                  <div className="text-nowrap">Sellar Credit</div>
                  <Icon icon="ARROW_TOP_RIGHT" size={1} className="ml-auto" />
                </div>

                <div className="w-full p-4">
                  {hasCreditLimitQuote ? (
                    <div className="flex flex-col gap-2">
                      <div className="w-full grow">
                        <div className="flex w-full items-center justify-center gap-1 rounded-lg border border-dashed border-zinc-300 bg-zinc-100 px-2 py-[6px]">
                          <Icon icon="LOCK" />
                          <span className="font-semibold">
                            {formattedCreditLimitQuote}
                          </span>
                        </div>
                      </div>
                      <p>
                        <span className="bg-linear-to-br from-[#B557F1] to-[#D213A6] bg-clip-text font-semibold text-transparent">
                          Unlock your credit account
                        </span>{" "}
                        to have access to up to 60 days credit across all of
                        your suppliers, with no interest or fees
                      </p>
                    </div>
                  ) : (
                    <p>One monthly payment, up to 60-day terms, no fees</p>
                  )}
                </div>
              </div>
            </div>
          ) : (
            <div
              className={twMerge("w-full", className)}
              onClick={() => setSellarCreditDialogActive(true)}
            >
              <div className="flex cursor-pointer flex-col items-center gap-2 rounded-lg border border-zinc-300 bg-white shadow-md xl:flex-row">
                <div className="flex h-12 flex-grow">
                  <div className="flex h-full w-full items-center gap-1 rounded-t-lg bg-gradient-to-br from-[#B557F1] to-[#D213A6] font-semibold text-white xl:w-auto xl:rounded-l-lg xl:rounded-tr-none xl:px-7">
                    <Icon
                      icon="SELLAR_NEW"
                      color="white"
                      size={0.7}
                      className="shrink-0"
                    />
                    <div className="text-nowrap">Sellar Credit</div>
                  </div>

                  <div className="flex items-center px-2">
                    {hasCreditLimitQuote ? (
                      <div className="flex w-full items-center gap-2">
                        <div className="flex min-w-fit items-center justify-center gap-1 rounded-lg border border-dashed border-zinc-300 bg-zinc-100 px-2 py-[6px] xl:mx-0 xl:w-auto xl:grow">
                          <Icon icon="LOCK" />
                          <span className="font-semibold">
                            {formattedCreditLimitQuote}
                          </span>
                        </div>
                        <p className="pt-0 xl:p-0">
                          Get up to 60 days credit across all of your suppliers,
                          with no interest or fees
                        </p>
                      </div>
                    ) : (
                      <p className="pt-0 xl:p-0">
                        One monthly payment, up to 60-day terms, no fees
                      </p>
                    )}
                  </div>

                  <div className="ml-auto flex items-center gap-1 bg-linear-to-br from-[#B557F1] to-[#D213A6] bg-clip-text px-4 font-semibold text-nowrap text-transparent">
                    {hasCreditLimitQuote ? "Unlock your credit" : "Get started"}
                    <Icon
                      icon="CHEVRON_RIGHT"
                      color="#D213A6"
                      size={1.1}
                      className="shrink-0"
                    />
                  </div>
                </div>
              </div>
            </div>
          )}
        </>
      )}

      <SellarCreditCheckDialog
        active={sellarCreditDialogActive}
        onDismiss={() => setSellarCreditDialogActive(false)}
      />
    </>
  );
};
